import { useId } from "react";
import styles from "../FeedbackDialog.module.css";

interface LongAnswerProps {
  label: string;
  hint: string;
  placeholder: string;
  value: string;
  maxLength: number;
  disabled: boolean;
  onChange: (value: string) => void;
}

/**
 * A labeled multi-line answer.
 *
 * Local to this module because the design system has no textarea yet - it
 * provides `TextField`, which is single-line. It borrows the design system's
 * own `.input` primitive and its `brunt-TextField-*` label, hint and field
 * classes, so it inherits the brand's border, focus ring and disabled state
 * rather than reimplementing them here. **When brunt-ui grows a `TextArea`,
 * this should be deleted in favor of it** - the classes it leans on are the
 * ones that component will use.
 *
 * `useId` rather than a prop: two of these render in the same dialog, and an id
 * passed in by hand is an id that eventually collides and silently unlabels one
 * of them.
 *
 * `maxLength` is a real attribute, not a check on submit. The browser stops the
 * keystroke, so the reporter cannot type a paragraph that will be truncated
 * server-side without their knowing.
 */
export function LongAnswerView({
  label,
  hint,
  placeholder,
  value,
  maxLength,
  disabled,
  onChange,
}: LongAnswerProps) {
  const id = useId();

  return (
    <div className="brunt-TextField-field">
      <label className="brunt-TextField-label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className={`input ${styles.textarea}`}
        rows={4}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="brunt-TextField-hint">{hint}</p>
    </div>
  );
}
